<template>
  <v-container
    class="pa-0"
    fluid
  >
    <v-combobox
      v-if="!isMobile"
      ref="combo"
      v-model="model"
      :filter="filter"
      :hide-no-data="!search"
      :items="items"
      :search-input.sync="search"
      hide-selected
      hide-details
      :label="$t('tag')"
      :outlined="!solo"
      :solo="solo"
      :flat="solo"
      :menu-props="{maxHeight: (isMobile ? 0 : 200)}"
      multiple
      dense
      small-chips
      :background-color="(solo ? '#e5e5e5' : null)"
      @blur="$emit('blur', model)"
      @click="openMobileDialog"
      @keydown.enter.prevent="handleCreateFromSearch"
    >
      <template v-slot:no-data>
        <v-list-item v-if="search && search.trim()">
          <v-list-item-content>
            <v-list-item-title>
              <strong>{{ search }}</strong>
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action>
            <v-btn
              small
              text
              @click.stop="handleCreateFromSearch"
            >
              {{ $t('create') }}
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </template>
      <template v-slot:selection="{attrs, item, parent, selected }">
        <v-chip
          v-if="item === Object(item)"
          v-bind="attrs"
          :color="item.color"
          :input-value="selected"
          label
          small
          class="px-1 mb-1 mt-1"
        >
          <span class="pr-2 white--text">
            {{ item.text }}
          </span>
          <v-icon
            small
            color="white"
            @click="parent.selectItem(item)"
          >
            mdi-close
          </v-icon>
        </v-chip>
      </template>
      <template v-slot:item="{ index, item }">
        <v-chip
          :color="item.color"
          dark
          label
          small
        >
          {{ item.text }}
        </v-chip>
        <v-spacer />
        <v-list-item-action
          class="d-inline"
          @click.stop
        >
          <v-btn
            icon
            small
            @click.stop.prevent="edit(index, item)"
          >
            <v-icon color="grey">
              mdi-pencil
            </v-icon>
          </v-btn>
          <v-btn
            small
            icon
            @click.stop.prevent="openStop(item)"
          >
            <v-icon color="pink">
              mdi-delete-outline
            </v-icon>
          </v-btn>
        </v-list-item-action>
      </template>
    </v-combobox>
    <v-autocomplete
      v-if="isMobile"
      v-model="mobileModel"
      clearable
      :items="mobileItems"
      no-filter
      height="38"
      item-text="text"
      item-value="id"
      :label="$t('tag')"
      :outlined="!solo"
      :solo="solo"
      :flat="solo"
      multiple
      hide-details
      :menu-props="{
        maxHeight: (isMobile ? 0 : 200),
        nudgeWidth: 0,
      }"
      @click="openMobileDialog"
    >
      <template v-slot:selection="{attrs, item, parent, selected }">
        <v-chip
          v-if="item === Object(item)"
          v-bind="attrs"
          :color="item.color"
          :input-value="selected"
          label
          small
          class="px-1 mb-1 mt-1"
        >
          <span class="pr-2 white--text">
            {{ item.text }}
          </span>
          <v-icon
            small
            color="white"
            @click="parent.selectItem(item)"
          >
            mdi-close
          </v-icon>
        </v-chip>
      </template>
    </v-autocomplete>
    <v-dialog
      v-model="mobileDialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      transition="dialog-bottom-transition"
      content-class="tag-mobile-picker-dialog"
    >
      <v-card class="tag-mobile-picker">
        <header class="tag-mobile-picker__header" dir="ltr">
          <button
            type="button"
            class="tag-mobile-picker__icon-btn"
            :aria-label="$t('back')"
            @click="mobileDialog = false"
          >
            <v-icon size="26" color="#37474F">mdi-arrow-left</v-icon>
          </button>
          <h2 class="tag-mobile-picker__title">{{ $t("tag") }}</h2>
          <span class="tag-mobile-picker__header-spacer" />
        </header>

        <div class="tag-mobile-picker__search" dir="rtl">
          <div class="tag-mobile-picker__search-box">
            <v-icon size="22" color="#78909C">mdi-magnify</v-icon>
            <input
              v-model="mobileSearch"
              type="search"
              class="tag-mobile-picker__search-input"
              :placeholder="$t('search')"
              autocomplete="off"
              @keydown.enter.prevent="handleCreateFromMobileSearch"
            />
            <button
              v-if="mobileSearch"
              type="button"
              class="tag-mobile-picker__search-clear"
              :aria-label="$t('clear')"
              @click="mobileSearch = ''"
            >
              <v-icon size="18" color="#78909C">mdi-close-circle</v-icon>
            </button>
          </div>
        </div>

        <div class="tag-mobile-picker__divider">
          <v-progress-linear
            v-if="isLoading"
            indeterminate
            color="#78909C"
            height="2"
            class="tag-mobile-picker__loader"
          />
        </div>

        <div
          v-if="mobileModel && mobileModel.length"
          class="tag-mobile-picker__selected"
          dir="rtl"
        >
          <v-chip
            v-for="(item, sIndex) in mobileModel"
            :key="'selected-tag-' + (item.id || item.text || sIndex)"
            :color="item.color || '#607D8B'"
            label
            small
            class="tag-mobile-picker__chip"
            close
            @click:close="toggleMobileTag(item)"
          >
            {{ item.text }}
          </v-chip>
        </div>

        <div class="tag-mobile-picker__body" dir="rtl">
          <v-list-item-group
            v-model="mobileModel"
            color="#607D8B"
            multiple
            class="tag-mobile-picker__group"
          >
            <v-list-item
              v-for="(item, index) in mobileItems"
              :key="item.id || index"
              :value="item"
              class="tag-mobile-picker__item"
            >
              <span class="tag-mobile-picker__index">{{ index + 1 }}</span>
              <v-list-item-content class="tag-mobile-picker__content">
                <v-list-item-title class="tag-mobile-picker__item-title">
                  {{ item.text }}
                </v-list-item-title>
              </v-list-item-content>
              <div class="tag-mobile-picker__actions" @click.stop>
                <button
                  type="button"
                  class="tag-mobile-picker__action-btn"
                  :aria-label="$t('edit')"
                  @click.stop.prevent="edit(index, item)"
                >
                  <v-icon size="20" color="#78909C">mdi-pencil</v-icon>
                </button>
                <button
                  type="button"
                  class="tag-mobile-picker__action-btn"
                  :aria-label="$t('remove')"
                  @click.stop.prevent="openStop(item)"
                >
                  <v-icon size="20" color="#E91E63">mdi-delete-outline</v-icon>
                </button>
                <v-icon
                  v-if="isMobileTagSelected(item)"
                  size="20"
                  color="#607D8B"
                >
                  mdi-check
                </v-icon>
              </div>
            </v-list-item>

            <div
              v-if="
                mobileItems &&
                mobileItems.length &&
                last_page > 1 &&
                last_page > page
              "
              v-observe-visibility="{ callback: visibilityChanged }"
              class="tag-mobile-picker__more"
            >
              <v-progress-circular
                :size="20"
                :width="2"
                color="#78909C"
                indeterminate
              />
            </div>

            <div
              v-if="!isLoading && (!mobileItems || !mobileItems.length)"
              class="tag-mobile-picker__empty"
            >
              {{ $t("no-data") }}
            </div>
          </v-list-item-group>
        </div>
      </v-card>
    </v-dialog>
      <v-dialog
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      v-model="dialog"
      width="300"
    >
      <v-card
        v-if="editing"
        class="pl-4"
      >
        <v-col>
          <v-color-picker
            v-model="editingColor"
            dot-size="25"
            hide-canvas
            hide-inputs
            hide-mode-switch
            hide-sliders
            mode="hexa"
            show-swatches
            swatches-max-height="132"
          />
        </v-col>
        <v-col
          class="mt-3 pr-7"
        >
          <v-text-field
            v-model="editingText"
            filled
            dense
            autofocus
            flat
            @keydown="keydown($event)"
          />
        </v-col>
        <v-card-actions>
          <v-btn
            color="primary"
            text
            @click="submit"
          >
            {{ $t('edit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
      <v-dialog
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      v-model="stop"
      width="400"
    >
      <v-card>
        <v-col>
          <v-btn
            icon
            color="grey darken-2"
            class="close-hover"
            @click="stop = false"
          >
            <v-icon large>
              mdi-close
            </v-icon>
          </v-btn>
        </v-col>
        <v-col>
          <v-img
            class="mx-auto"
            width="200"
            src="@/assets/stop.svg"
          />
        </v-col>
        <v-col
          class="mt-3 pr-7"
        >
          <p class="mx-auto font-weight-medium">
            {{ $t('alert-delete-tag') }}
          </p>
        </v-col>
        <v-card-actions>
          <v-row class="ma-0">
            <v-col cols="6">
              <v-btn
                color="primary"
                @click="remove"
              >
                {{ $t('remove') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                color="grey"
                outlined
                class="float-left"
                @click="stop = false"
              >
                {{ $t('cancel') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import General from '@/Mixins/general'
export default {
  mixins: [General],
  props: ['type', 'input', 'solo', 'height'],
  data: () => ({
    activator: null,
    attach: null,
    deleteItem: null,
    stop: false,
    formHasErrors: false,
    dialog: false,
    colors: ['green', 'red', 'blue', 'purple', 'pink', 'orange', 'deep-purple', 'teal', 'cyan', 'indigo', 'orange', 'amber', 'brown', 'deep-orange', 'grey', 'blue-grey'],
    editing: null,
    editingText: null,
    oldText: null,
    editingColor: null,
    editingIndex: -1,
    errorMessages: '',
    items: [],
    nonce: 1,
    mobileDialog: false,
    timer: null,
    mobileSearch: null,
    mobileModel: [],
    last_page: null,
    page: 1,
    mobileItems: [],
    isLoading: false,
    menu: false,
    model: [],
    x: 0,
    search: null,
    y: 0
  }),
  watch: {
    // model (val, prev) {
    //   if (val) {
    //     if (val.length === prev.length) {
    //       this.$emit('input', val)
    //       return
    //     }
    //     this.model = val.map(v => {
    //       if (typeof v === 'string') {
    //         v = {
    //           text: v,
    //           color: this.colors[this.nonce - 1],
    //           type: this.type,
    //           ref_id: null
    //         }

    //         this.items.push(v)
    //         this.create(v)
    //         this.nonce++
    //       }
    //       this.$emit('input', this.model)
    //       return v
    //     })
    //   }
    // },
    model (val) {
      this.$emit('input', val)
    },
    input (val, oldVal) {
      if (this.input) {
        if (this.isMobile) {
          this.mobileModel = this.input
        } else {
          this.model = this.input
        }
      } else {
        if (this.isMobile) {
          this.mobileModel = []
        } else {
          this.model = []
        }
      }
    },
    mobileSearch () {
      this.page = 1
      this.setInitial = false
      this.items = []
      this.mobileItems = []
      this.getItems()
    },
    mobileModel (val) {
      this.$emit('input', val)
      // this.model = this.mobileDialog
    }
  },
  mounted () {
    this.getItems()
    if (this.input) {
      if (this.isMobile) {
        this.mobileModel = this.input
      } else {
        this.model = this.input
      }
    }
    this.items.push({ header: this.$t('Select an option or create one') })
  },
  methods: {
    handleCreateFromSearch () {
      const text = (this.search || '').trim()
      if (!text) return

      // Check if tag already exists
      const existing = this.items.find(i => i.text === text)
      if (existing) {
        if (!this.model.includes(existing)) {
          this.model = [...this.model, existing]
        }
        this.search = ''
        return
      }

      const newTag = {
        text,
        color: this.colors[(this.nonce || 1) - 1],
        type: this.type,
        ref_id: null
      }

      this.items.push(newTag)
      this.model = [...this.model, newTag]
      this.create(newTag) // your existing create logic
      this.nonce = (this.nonce || 1) + 1
      this.search = ''
    },
    visibilityChanged (e) {
      e && this.getItems()
    },
    openMobileDialog () {
      if (this.isMobile) {
        this.mobileDialog = true
        if (this.page === 1 && this.mobileItems.length === 0) {
          this.getItems()
        }
      }
    },
    isMobileTagSelected (item) {
      if (!item || !Array.isArray(this.mobileModel)) return false
      return this.mobileModel.some((selected) => {
        if (!selected) return false
        if (selected.id != null && item.id != null) {
          return selected.id === item.id
        }
        return selected.text === item.text
      })
    },
    toggleMobileTag (item) {
      if (!item) return
      const current = Array.isArray(this.mobileModel) ? [...this.mobileModel] : []
      const index = current.findIndex((selected) => {
        if (!selected) return false
        if (selected.id != null && item.id != null) {
          return selected.id === item.id
        }
        return selected.text === item.text
      })
      if (index >= 0) {
        current.splice(index, 1)
      } else {
        current.push(item)
      }
      this.mobileModel = current
    },
    handleCreateFromMobileSearch () {
      const text = (this.mobileSearch || '').trim()
      if (!text) return

      const existing = this.mobileItems.find((i) => i && i.text === text)
      if (existing) {
        if (!this.isMobileTagSelected(existing)) {
          this.toggleMobileTag(existing)
        }
        this.mobileSearch = ''
        return
      }

      const newTag = {
        text,
        color: this.colors[(this.nonce || 1) - 1],
        type: this.type,
        ref_id: null
      }
      this.mobileItems = [newTag, ...this.mobileItems]
      this.toggleMobileTag(newTag)
      this.create(newTag)
      this.nonce = (this.nonce || 1) + 1
      this.mobileSearch = ''
    },
    create (item) {
      var app = this
      app.isLoading = true
      app.$axios.post(this.$endpoint + 'tags', {
        text: item.text,
        color: item.color,
        type: item.type,
        ref_id: item.ref_id
      })
        .then(res => {
        })
        .catch(err => {
          app.serverErrorHandeler(err)
        })
        .finally(() => (app.isLoading = false))
    },
    getItems () {
      var app = this
      app.isLoading = true
      clearTimeout(app.timer)
      app.timer = setTimeout(function () {
        app.$axios.get(app.$endpoint + 'tags/show', {
          params: {
            search: (app.isMobile ? app.mobileSearch : null)
          }
        })
          .then(res => {
            if (!app.isMobile) {
              app.items = [
                ...app.items,
                ...Array.from(res.data.data)
              ]
            } else {
              app.mobileItems = [
                ...app.mobileItems,
                ...Array.from(res.data.data)
              ]
            }
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(function () {
            app.isLoading = false
            app.loading = false
          })
      }, 1000)
    },
    keydown: function (event) {
      switch (event.keyCode) {
        case 13:
          this.submit()
          break
      }
    },
    submit () {
      var index
      if (!this.isMobile) {
        index = this.items.findIndex(x => x.text === this.oldText)
        this.items[index].text = this.editingText
        this.items[index].color = this.editingColor
      } else {
        index = this.mobileItems.findIndex(x => x.text === this.oldText)
        this.mobileItems[index].text = this.editingText
        this.mobileItems[index].color = this.editingColor
      }
      this.dialog = false
      var app = this
      app.$axios.get(this.$endpoint + 'tags/' + this.editingText + '/edit', {
        params: {
          oldText: app.oldText,
          text: this.editingText,
          color: this.editingColor
        }
      })
        .then(res => {
        })
        .catch(err => {
          app.serverErrorHandeler(err)
        })
        .finally(() => (app.isLoading = false))
    },
    edit (index, item) {
      this.editing = item
      this.editingIndex = index
      this.editingText = item.text
      this.editingColor = item.color
      this.oldText = item.text
      this.dialog = true
    },
    openStop (item) {
      this.deleteItem = item
      this.stop = true
    },
    remove () {
      var index
      if (!this.isMobile) {
        index = this.items.findIndex(x => x.text === this.deleteItem.text)
        this.items.splice(index, 1)
      } else {
        index = this.mobileItems.findIndex(x => x.text === this.deleteItem.text)
        this.mobileItems.splice(index, 1)
      }
      this.stop = false
      var app = this
      app.$axios.delete(this.$endpoint + 'tags/' + this.deleteItem.text)
        .then(res => {
        })
        .catch(err => {
          var error = err.response
          if (error.status === 500) {}
          if (error.status === 401) {
            app.$router.push({ name: 'logout' })
          }
        })
        .finally(() => (app.isLoading = false))
    },
    filter (item, queryText, itemText) {
      if (item.header) return false

      const hasValue = val => val != null ? val : ''

      const text = hasValue(itemText)
      const query = hasValue(queryText)

      return text.toString()
        .toLowerCase()
        .indexOf(query.toString().toLowerCase()) > -1
    }
  }
}
</script>

<style scoped>
.tag-mobile-picker {
  --picker-nude: #eceff2;
  --picker-nude-soft: #f3f5f7;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100vh;
  background: var(--picker-nude-soft) !important;
  border-radius: 0 !important;
}

.tag-mobile-picker__header {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: 44px 1fr 44px;
  align-items: center;
  gap: 4px;
  min-height: 56px;
  padding: 8px 10px;
  background: #fff;
  border-bottom: 1px solid #eceff1;
}

.tag-mobile-picker__title {
  margin: 0;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: #263238;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tag-mobile-picker__header-spacer {
  width: 40px;
  height: 40px;
}

.tag-mobile-picker__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.tag-mobile-picker__icon-btn:active {
  background: var(--picker-nude);
}

.tag-mobile-picker__search {
  flex: 0 0 auto;
  padding: 12px;
  background: #fff;
}

.tag-mobile-picker__search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  min-height: 48px;
  padding: 0 12px;
  border-radius: 10px;
  background: var(--picker-nude);
  border: 1px solid #e0e4e8;
  box-sizing: border-box;
}

.tag-mobile-picker__search-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 15px;
  font-weight: 500;
  color: #263238;
  text-align: right;
  line-height: 1.4;
}

.tag-mobile-picker__search-input::placeholder {
  color: #90a4ae;
  font-weight: 400;
}

.tag-mobile-picker__search-input::-webkit-search-cancel-button {
  display: none;
}

.tag-mobile-picker__search-clear {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
}

.tag-mobile-picker__divider {
  flex: 0 0 auto;
  position: relative;
  height: 1px;
  background: #eceff1;
}

.tag-mobile-picker__loader {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0 !important;
}

.tag-mobile-picker__selected {
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 12px 0;
  background: var(--picker-nude-soft);
}

.tag-mobile-picker__chip {
  margin: 0 !important;
}

.tag-mobile-picker__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 10px 12px 20px;
}

.tag-mobile-picker__group {
  background: transparent;
}

.tag-mobile-picker__item {
  min-height: 52px !important;
  margin: 0 0 8px !important;
  padding: 8px 12px !important;
  border-radius: 8px !important;
  background: #fff !important;
  border: 1px solid #e8eef3 !important;
  box-shadow: none !important;
}

.tag-mobile-picker__item.v-list-item--active {
  border-color: #c5ced6 !important;
  background: #eceff2 !important;
}

.tag-mobile-picker__index {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  margin-left: 10px;
  padding: 0 6px;
  border-radius: 6px;
  background: #eceff1;
  color: #546e7a;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.tag-mobile-picker__content {
  padding: 0 !important;
}

.tag-mobile-picker__item-title {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #263238 !important;
  text-align: right;
}

.tag-mobile-picker__actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin: 0;
}

.tag-mobile-picker__action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.tag-mobile-picker__more {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}

.tag-mobile-picker__empty {
  text-align: center;
  font-size: 13px;
  color: #90a4ae;
  padding: 24px 0;
}
</style>
